import * as React from "react";
import { connect } from "react-redux";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import AddBoxIcon from "@mui/icons-material/AddBox";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import AllInboxIcon from "@mui/icons-material/AllInbox";
import EmailIcon from "@mui/icons-material/Email";
import RssFeed from "@mui/icons-material/RssFeed";
import EventNoteIcon from "@mui/icons-material/EventNote";
import PaymentsIcon from "@mui/icons-material/Payments";
import { Box, ListItemButton } from "@mui/material";
import { push } from "connected-react-router";
import DashboardIcon from "@mui/icons-material/Dashboard";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DomainIcon from "@mui/icons-material/Domain";
import SettingsIcon from "@mui/icons-material/Settings";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import AssessmentIcon from "@mui/icons-material/Assessment";
import InventoryIcon from "@mui/icons-material/Inventory";
import SmsIcon from "@mui/icons-material/Sms";
import PeopleIcon from "@mui/icons-material/People";
import PaidIcon from "@mui/icons-material/Paid";
import ShopIcon from "@mui/icons-material/Shop";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import NotificationImportantIcon from "@mui/icons-material/NotificationImportant";
import { MenuBook } from "@mui/icons-material";
import FlaskIcon from "../../../assets/icons/Flask.icon";
import BillIcon from "../../../assets/icons/Bill.icon";
import { tl } from "../../components/translate";
import { getRCFromState } from "../../actions/resourceCentre";
import BedIcon from "../../../assets/icons/Bed.icon";
import PrescriptionIcon from "../../../assets/icons/Prescription.icon";
import { PermissionGroups, UserMode } from "../../interfaces/User";
import { UserViewMode } from "../../interfaces/UserContext";
import Can from "../Policy/Can";
import { clearDraft } from "../../actions/bill";
import { clearDraft as clearFeedDraft } from "../../actions/feed";
import { RootState } from "../../store";

import { clearJournalVoucher } from "../../slices/journalEntry";
import mixpanelAnalytics from "../../mixpanel-analytics/mixpanelAnalytics";
import EVENT from "../../mixpanel-analytics/event";
import useCurrentResourceCentre from "../../hooks/useCurrentResourceCentre";
import DaybookNotSubscribedPanel from "../Daybook/DaybookNotSubscribedPanel";
import useIsUserRcAdmin from "../../hooks/useIsUserRcAdmin";
import useModuleSubscriptions from "../../hooks/useModuleSubscriptions";

// @ts-expect-error - css
import styles from "./styles.module.css";
// @ts-expect-error - png
import logo from "../../../assets/images/okhati_logo_textless_white_shadowed.png";
// @ts-expect-error - png
import logoGreenFull from "../../../assets/images/okhati_full_green.png";

const selectedClassName = (route, router) =>
  router.location.pathname?.indexOf(route) === 0 ? styles.selected : "";

const selectedClassNameForRoutes = (routes: string[], router) =>
  routes.some((route) => router.location.pathname?.indexOf(route) === 0) ? styles.selected : "";

export const isResourceCentreUser = (mode: UserMode): boolean =>
  [UserMode.Employee, UserMode.ServiceProvider].includes(mode);

export const isSuperAdmin = (permissionGroup: string): boolean => permissionGroup === "superAdmin";

export const isReadOnlyUser = (permissionGroup: string): boolean =>
  permissionGroup === PermissionGroups.ReasourceCentreReadOnlyEmployee ||
  permissionGroup === PermissionGroups.ResourceCentreReportOnlyAccessEmployee;

export const isReportOnlyUser = (permissionGroup: string): boolean =>
  permissionGroup === PermissionGroups.ResourceCentreReportOnlyAccessEmployee;

export const isResourceCentreCampaignOnlyEmployee = (permissionGroup: string): boolean =>
  permissionGroup === PermissionGroups.ResourceCentreCampaignOnlyEmployee;

export const isSelfDataAccessEmployee = (permissionGroup: string): boolean =>
  permissionGroup === PermissionGroups.SelfDataAccessEmployee;

export const isAdmin = (mode: UserMode): boolean =>
  [UserMode.SuperAdmin, UserMode.Admin].includes(mode);

export const isSupportAdmin = (mode: UserMode, permissionGroup: PermissionGroups): boolean =>
  isAdmin(mode) && permissionGroup === PermissionGroups.SupportAdmin;

export const isSuperAdminAgent = (mode: UserMode, permissionGroup: PermissionGroups): boolean =>
  isAdmin(mode) && permissionGroup === PermissionGroups.SuperAdminAgent;

const MenuItems = ({
  goto,
  resourceCentreId,
  router,
  clearBillDraft,
  clearFeedDraftAction,
  clearJV,
  adminImpersonating,
  viewMode
}) => {
  const rc = useCurrentResourceCentre();
  const isUserRcAdmin = useIsUserRcAdmin();
  const { isPayrollSubscribed, isDaybookSubscribed, isAttendanceSubscribed } =
    useModuleSubscriptions();

  const gotoAndClearDrafts = (url) => {
    goto(url);
    clearBillDraft();
    clearFeedDraftAction();
    clearJV();
  };

  const [showDaybookNotSubscribedPanel, setShowDaybookNotSubscribedPanel] = React.useState(false);

  return (
    <>
      {adminImpersonating && (
        <Box
          position="fixed"
          top="2px"
          left="calc( 50% - 50px)"
          style={{ backgroundColor: "red", color: "white" }}
          padding="10px"
          fontWeight={700}
          borderRadius="3px"
        >
          {" "}
          Impersonation Mode
        </Box>
      )}
      {showDaybookNotSubscribedPanel && (
        <DaybookNotSubscribedPanel
          setShowDaybookNotSubscribedPanel={setShowDaybookNotSubscribedPanel}
        />
      )}
      <div style={{ flexGrow: 1 }}>
        <Box sx={{ display: { xs: "block", md: "none" } }}>
          <img className={styles.logoMobileMenu} src={logoGreenFull} alt="main-logo" />
        </Box>
        <List className={styles.list}>
          <Can
            policyAccessKey="allow"
            superAdminPassReverse
            supportAccountAdminPass
            publicAdminPassReverse
          >
            <ListItemButton
              data-testmation="sideBarDashboard"
              className={`${styles.menuItem} ${selectedClassName("/dashboard", router)}`}
              onClick={() => {
                mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_DASHBOARD, {
                  rcId: rc?.id,
                  rcName: rc?.name
                });
                gotoAndClearDrafts("/dashboard");
              }}
            >
              <DashboardIcon className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button">
                {tl("navigation.dashboard")}
              </Typography>
            </ListItemButton>
          </Can>
          <Can
            policyAccessKey={["booking:listBooking", "booking:listSchedule"]}
            partialCheck
            superAdminPassReverse
          >
            <ListItemButton
              data-testmation="sideBarCalendar"
              className={`${styles.menuItem} ${selectedClassName("/calendar", router)}`}
              onClick={() => {
                gotoAndClearDrafts("/calendar");
                mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_CALENDAR, {
                  rcId: rc?.id,
                  rcName: rc?.name
                });
              }}
            >
              <CalendarTodayIcon className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button">
                {tl("navigation.calendar")}
              </Typography>
            </ListItemButton>
          </Can>
          <Can policyAccessKey="bill:listBill" superAdminPassReverse>
            <ListItemButton
              data-testmation="sideBarBilling"
              className={`${styles.menuItem} ${selectedClassName("/billing", router)}`}
              onClick={() => {
                mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_BILLING, {
                  rcId: rc?.id,
                  rcName: rc?.name
                });
                goto("/billing/new");
              }}
            >
              <BillIcon className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button">
                {tl("navigation.billing")}
              </Typography>
            </ListItemButton>
          </Can>
          <Can policyAccessKey="deny" superAdminPass>
            <ListItemButton
              data-testmation="sideBarClinics"
              className={`${styles.menuItem} ${selectedClassName("/resourcecentres", router)}`}
              onClick={() => {
                if (rc) {
                  mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_CLINICS, {
                    rcId: rc?.id,
                    rcName: rc?.name
                  });
                }
                gotoAndClearDrafts("/resourcecentres");
              }}
            >
              <DomainIcon className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button">
                {tl("navigation.clinics")}
              </Typography>
            </ListItemButton>
          </Can>
          <Can policyAccessKey="opd:listOpd" superAdminPassReverse>
            <ListItemButton
              data-testmation="sideBarOpd"
              className={`${styles.menuItem} ${selectedClassName("/assessment", router)}`}
              onClick={() => {
                mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_OPD, {
                  rcId: rc?.id,
                  rcName: rc?.name
                });
                gotoAndClearDrafts(`/assessment/new`);
              }}
            >
              <PrescriptionIcon className={styles.menuItemIcon} />

              <Typography className={styles.label} variant="button">
                {tl("navigation.opd")}
              </Typography>
            </ListItemButton>
          </Can>
          <Can policyAccessKey="lab:listLab" superAdminPassReverse>
            <ListItemButton
              data-testmation="sideBarLab"
              className={`${styles.menuItem} ${selectedClassName("/lab", router)}`}
              onClick={() => {
                mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_LAB, {
                  rcId: rc?.id,
                  rcName: rc?.name
                });
                gotoAndClearDrafts("/lab/labRecords/all?status=Ordered");
              }}
            >
              <FlaskIcon className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button">
                {tl("navigation.lab")}
              </Typography>
            </ListItemButton>
          </Can>
          <Can policyAccessKey="services:listService" superAdminPassReverse>
            <ListItemButton
              data-testmation="sideBarServices"
              className={`${styles.menuItem} ${selectedClassName("/services", router)}`}
              onClick={() => {
                mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_SERVICES, {
                  rcId: rc?.id,
                  rcName: rc?.name
                });
                gotoAndClearDrafts("/services");
              }}
            >
              <NoteAddIcon className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button">
                {tl("navigation.services")}
              </Typography>
            </ListItemButton>
          </Can>
          <Can policyAccessKey="deny" superAdminPass>
            <ListItemButton
              data-testmation="sideBarOnDemandProducts"
              className={`${styles.menuItem} ${selectedClassName("/onDemandProduct", router)}`}
              onClick={() => {
                mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_ON_DEMAND_PRODUCT);
                gotoAndClearDrafts("/onDemandProduct");
              }}
            >
              <NoteAddIcon className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button" style={{ textAlign: "center" }}>
                {tl("navigation.onDemandProducts")}
              </Typography>
            </ListItemButton>
          </Can>
          <Can policyAccessKey="clients:listClient" superAdminPassReverse>
            <ListItemButton
              data-testmation="sideBarClients"
              className={`${styles.menuItem} ${selectedClassName("/clients", router)}`}
              onClick={() => {
                mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_CLIENTS, {
                  rcId: rc?.id,
                  rcName: rc?.name
                });
                gotoAndClearDrafts("/clients");
              }}
            >
              <PeopleRoundedIcon className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button">
                {tl("navigation.clients")}
              </Typography>
            </ListItemButton>
          </Can>
          <Can policyAccessKey="deny" superAdminAgentPass>
            <ListItemButton
              data-testmation="masterDataControl"
              className={`${styles.menuItem} ${selectedClassName("/masterDataControl", router)}`}
              onClick={() => {
                mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_MASTER_DATA_CONTROL);
                gotoAndClearDrafts("/masterDataControl");
              }}
            >
              <PeopleRoundedIcon className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button">
                Master Data Control
              </Typography>
            </ListItemButton>
          </Can>
          <Can policyAccessKey="deny" superAdminAgentPass>
            <ListItemButton
              data-testmation="smsProviders"
              className={`${styles.menuItem} ${selectedClassName("/smsProviders", router)}`}
              onClick={() => {
                gotoAndClearDrafts("/smsProviders");
              }}
            >
              <SmsIcon className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button">
                SMS
              </Typography>
            </ListItemButton>
          </Can>
          <Can policyAccessKey="stock:listStock" superAdminPassReverse>
            {viewMode === UserViewMode.PHARMACY ? (
              <>
                <ListItemButton
                  data-testmation="sideBarProducts"
                  className={`${styles.menuItem} ${selectedClassName(
                    "/stock/stockProducts",
                    router
                  )}`}
                  onClick={() => {
                    mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_STOCK_PRODUCTS, {
                      rcId: rc?.id,
                      rcName: rc?.name
                    });
                    gotoAndClearDrafts("/stock/stockProducts");
                  }}
                >
                  <AllInboxIcon className={styles.menuItemIcon} />
                  <Typography className={styles.label} variant="button">
                    {tl("stock.products")}
                  </Typography>
                </ListItemButton>

                <ListItemButton
                  data-testmation="sideBarInternalInventory"
                  className={`${styles.menuItem} ${selectedClassName(
                    "/stock/stockInternal",
                    router
                  )}`}
                  onClick={() => {
                    mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_INTERNAL_INVENTORY, {
                      rcId: rc?.id
                    });
                    gotoAndClearDrafts("/stock/stockInternal");
                  }}
                >
                  <InventoryIcon className={styles.menuItemIcon} />
                  <Typography className={styles.label} variant="button">
                    {tl("stock.internalInventory")}
                  </Typography>
                </ListItemButton>

                <ListItemButton
                  data-testmation="sideBarStockTransactions"
                  className={`${styles.menuItem} ${selectedClassName(
                    "/stock/stockTransactions",
                    router
                  )}`}
                  onClick={() => {
                    mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_STOCK_TRANSACTIONS, {
                      rcId: rc?.id
                    });
                    gotoAndClearDrafts("/stock/stockTransactions");
                  }}
                >
                  <PaidIcon className={styles.menuItemIcon} />
                  <Typography className={styles.label} variant="button">
                    {tl("stock.stockTransactions")}
                  </Typography>
                </ListItemButton>
              </>
            ) : (
              <ListItemButton
                data-testmation="sideBarStock"
                className={`${styles.menuItem} ${selectedClassName("/stock", router)}`}
                onClick={() => {
                  mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_STOCK, {
                    rcId: rc?.id,
                    rcName: rc?.name
                  });
                  gotoAndClearDrafts("/stock/stockProducts");
                }}
              >
                <AllInboxIcon className={styles.menuItemIcon} />
                <Typography className={styles.label} variant="button">
                  {tl("navigation.stock")}
                </Typography>
              </ListItemButton>
            )}
          </Can>
          {viewMode === UserViewMode.PHARMACY && (
            <Can policyAccessKey="clinic:listSuppliers" superAdminPassReverse>
              <ListItemButton
                data-testmation="sideBarSuppliers"
                className={`${styles.menuItem} ${selectedClassName("/suppliers", router)}`}
                onClick={() => {
                  mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_SUPPLIER, {
                    rcId: rc?.id,
                    rcName: rc?.name
                  });
                  gotoAndClearDrafts("/suppliers");
                }}
              >
                <ShopIcon className={styles.menuItemIcon} />
                <Typography className={styles.label} variant="button">
                  {tl("employee.suppliers")}
                </Typography>
              </ListItemButton>
            </Can>
          )}
          <Can
            policyAccessKey={[
              "campaign:listCampaign",
              "campaign:listTemplate",
              "campaign:listAudience"
            ]}
            partialCheck
            superAdminPassReverse
          >
            <ListItemButton
              data-testmation="sideBarCampaign"
              className={`${styles.menuItem} ${selectedClassName("/campaign", router)}`}
              onClick={() => {
                mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_CAMPAIGN, {
                  rcId: rc?.id,
                  rcName: rc?.name
                });
                gotoAndClearDrafts("/campaign/campaigns");
              }}
            >
              <EmailIcon className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button">
                {tl("navigation.messaging")}
              </Typography>
            </ListItemButton>
          </Can>
          <Can policyAccessKey="deny" superAdminPass>
            <ListItemButton
              data-testmation="sideBarFeed"
              className={`${styles.menuItem} ${selectedClassName("/feeds", router)}`}
              onClick={() => {
                mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_FEED);
                gotoAndClearDrafts("/feeds");
              }}
            >
              <RssFeed className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button">
                Feeds
              </Typography>
            </ListItemButton>
          </Can>
          <Can policyAccessKey="medical:listMedical" superAdminPassReverse>
            <ListItemButton
              data-testmation="sideBarMedical"
              className={`${styles.menuItem} ${selectedClassName("/medical", router)}`}
              onClick={() => {
                mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_MEDICAL, {
                  rcId: rc?.id,
                  rcName: rc?.name
                });
                gotoAndClearDrafts("/medical");
              }}
            >
              <AddBoxIcon className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button">
                {tl("navigation.medical")}
              </Typography>
            </ListItemButton>
          </Can>
          <Can policyAccessKey="deny" superAdminAgentPass>
            <ListItemButton
              data-testmation="sideBarBookings"
              className={`${styles.menuItem} ${selectedClassName("/bookings", router)}`}
              onClick={() => {
                mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_BOOKINGS, {
                  rcId: rc?.id,
                  rcName: rc?.name
                });
                gotoAndClearDrafts("/bookings");
              }}
            >
              <EventNoteIcon className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button">
                {tl("navigation.bookings")}
              </Typography>
            </ListItemButton>
          </Can>
          <Can policyAccessKey="deny" superAdminPass>
            <ListItemButton
              data-testmation="accountsSettings"
              className={`${styles.menuItem} ${selectedClassName("/accountSettings", router)}`}
              onClick={() => {
                mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_ACCOUNT_SETTINGS);
                gotoAndClearDrafts("/accountSettings");
              }}
            >
              <MonetizationOnIcon className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button">
                {tl("navigation.accountSettings")}
              </Typography>
            </ListItemButton>
          </Can>
          <Can policyAccessKey="ipd:listIpd" superAdminPassReverse>
            <ListItemButton
              data-testmation="sideBarIPD"
              className={`${styles.menuItem} ${selectedClassNameForRoutes(["/ipd"], router)}`}
              onClick={() => {
                mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_IPD, {
                  rcId: rc?.id,
                  rcName: rc?.name
                });
                gotoAndClearDrafts("/ipd/dashboard");
              }}
            >
              <BedIcon className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button">
                {tl("navigation.ipd")}
              </Typography>
            </ListItemButton>
          </Can>
          <Can policyAccessKey="hmis:listHmis" superAdminPassReverse>
            <ListItemButton
              data-testmation="sideBarHmis"
              className={`${styles.menuItem} ${selectedClassName("/hmis", router)}`}
              onClick={() => {
                mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_HMIS, {
                  rcId: rc?.id,
                  rcName: rc?.name
                });
                gotoAndClearDrafts("/hmis/mulDarta");
              }}
            >
              <BedIcon className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button">
                {tl("navigation.hmis")}
              </Typography>
            </ListItemButton>
          </Can>
          <Can policyAccessKey="deny" superAdminPass>
            <ListItemButton
              data-testmation="sideBarPermissions"
              className={`${styles.menuItem} ${selectedClassName("/userGroups", router)}`}
              onClick={() => {
                gotoAndClearDrafts("/userGroups");
              }}
            >
              <DomainIcon className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button">
                {tl("sidebar.permission")}
              </Typography>
            </ListItemButton>
          </Can>
          <Can policyAccessKey="account:listAccount" superAdminPassReverse>
            <ListItemButton
              data-testmation="sidebarAccount"
              className={`${styles.menuItem} ${selectedClassName(
                "/accounts/vouchers/sales",
                router
              )}`}
              onClick={() => {
                mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_ACCOUNT_VOUCHER_SALES, {
                  rcId: rc?.id,
                  rcName: rc?.name
                });
                gotoAndClearDrafts("/accounts/vouchers/sales");
              }}
            >
              <MonetizationOnIcon className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button">
                {tl("accounts")}
              </Typography>
            </ListItemButton>
          </Can>
          <Can policyAccessKey="deny" superAdminPass supportAccountAdminPass publicAdminPass>
            <ListItemButton
              data-testmation="sideBarPublic"
              className={`${styles.menuItem} ${selectedClassName(
                "/public/verificationCodes",
                router
              )}`}
              onClick={() => {
                gotoAndClearDrafts(`/public/verificationCodes`);
              }}
            >
              <DomainIcon className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button">
                Okhati Public
              </Typography>
            </ListItemButton>
          </Can>

          {isDaybookSubscribed && (
            <Can policyAccessKey="allow" superAdminPassReverse supportAccountAdminPass>
              <Box sx={{ position: "relative" }}>
                <ListItemButton
                  data-testmation="sideBarDaybook"
                  className={`${styles.menuItem} ${selectedClassName("/daybook", router)}`}
                  sx={{ cusor: "pointer" }}
                  onClick={() => {
                    mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_DAYBOOK, {
                      rcId: rc?.id,
                      rcName: rc?.name
                    });

                    gotoAndClearDrafts("/daybook");
                  }}
                >
                  <MenuBook className={styles.menuItemIcon} />
                  <Typography className={styles.label} variant="button">
                    {tl("navigation.daybook")}
                  </Typography>
                </ListItemButton>
              </Box>
            </Can>
          )}

          {isUserRcAdmin && isAttendanceSubscribed && (
            <ListItemButton
              className={`${styles.menuItem} ${selectedClassName("/attendance/dashboard", router)}`}
              onClick={() => {
                gotoAndClearDrafts("/attendance/dashboard");
              }}
            >
              <PeopleIcon className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button">
                Attendance
              </Typography>
            </ListItemButton>
          )}

          {isUserRcAdmin && isPayrollSubscribed && (
            <ListItemButton
              className={`${styles.menuItem} ${selectedClassName("/payroll", router)}`}
              onClick={() => {
                gotoAndClearDrafts("/payroll");
              }}
            >
              <PaymentsIcon className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button">
                Payroll
              </Typography>
            </ListItemButton>
          )}

          <Can policyAccessKey="deny" superAdminAgentPass>
            <ListItemButton
              className={`${styles.menuItem} ${selectedClassName("/importDemoData", router)}`}
              onClick={() => {
                gotoAndClearDrafts("/importDemoData");
              }}
            >
              <SmsIcon className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button">
                RC Data
              </Typography>
            </ListItemButton>
          </Can>

          <Can policyAccessKey="report:*" superAdminPass>
            <ListItemButton
              data-testmation="sideBarReports"
              className={`${styles.menuItem} ${selectedClassName("/reports", router)}`}
              onClick={() => {
                mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_REPORTS, { rcId: rc?.id });
                gotoAndClearDrafts("/reports");
              }}
            >
              <AssessmentIcon className={styles.menuItemIcon} />
              <Typography className={styles.label} variant="button">
                {tl("navigation.reports")}
              </Typography>
            </ListItemButton>
          </Can>
        </List>
      </div>
      <div className={styles.bottom}>
        <Can policyAccessKey="allow" superAdminPassReverse publicAdminPassReverse>
          <ListItemButton
            data-testmation="sideBarClinics"
            className={`${styles.menuItem} ${selectedClassName("/resourcecentres", router)}`}
            onClick={() => {
              mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_CLINIC_DETAILS, {
                rcId: rc?.id,
                rcName: rc?.name
              });
              gotoAndClearDrafts(`/resourcecentres/${resourceCentreId}/details`);
            }}
          >
            <SettingsIcon className={styles.menuItemIcon} />
            <Typography className={styles.label} variant="button">
              {tl("navigation.settings")}
            </Typography>
          </ListItemButton>
        </Can>
        <Can policyAccessKey="deny" superAdminPass>
          <ListItemButton
            data-testmation="sideBarNotifications"
            className={`${styles.menuItem} ${selectedClassName(
              "/resourceCentreNotifications",
              router
            )}`}
            onClick={() => {
              mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_NOTIFICATIONS);
              gotoAndClearDrafts(`/resourceCentreNotifications`);
            }}
          >
            <NotificationImportantIcon className={styles.menuItemIcon} />
            <Typography className={styles.label} variant="button">
              {tl("navigation.notification")}
            </Typography>
          </ListItemButton>
        </Can>
        <Can policyAccessKey="deny" superAdminPass supportAccountAdminPass>
          <ListItemButton
            data-testmation="sideBarPayments"
            className={`${styles.menuItem} ${selectedClassName(
              "/resourceCentreSubscriptions",
              router
            )}`}
            onClick={() => {
              mixpanelAnalytics.track(EVENT.SIDEBAR_GO_TO_SUBSCRIPTION);
              gotoAndClearDrafts(`/resourceCentreSubscriptions`);
            }}
          >
            <DomainIcon className={styles.menuItemIcon} />
            <Typography className={styles.label} variant="button">
              {tl("sidebar.subscription")}
            </Typography>
          </ListItemButton>
        </Can>
      </div>
    </>
  );
};

const SideBar = (props) => {
  const { hamMenuOpen, toggleHamMenu, gotoPath, permissionGroup, viewMode } = props;

  return (
    <>
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <Drawer
          open={hamMenuOpen}
          onClose={() => toggleHamMenu(false)}
          className={styles.drawerWrapper}
          classes={{
            paper: styles.drawer
          }}
        >
          <MenuItems
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
            goto={(path) => {
              gotoPath(path);
              toggleHamMenu(false);
            }}
            viewMode={viewMode}
            permissionGroup={permissionGroup}
          />
        </Drawer>
      </Box>
      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <Drawer
          variant="permanent"
          className={styles.drawerWrapper}
          anchor="left"
          classes={{
            paper: styles.drawer
          }}
        >
          <Box
            className={styles.logo}
            sx={{ flexBasis: 60, mt: 1 }}
            onClick={() => {
              if (!isReadOnlyUser(permissionGroup)) gotoPath("/");
            }}
          >
            <img className={styles.logoimg} src={logo} alt="logo" />
          </Box>
          <MenuItems
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
            goto={(path) => {
              gotoPath(path);
              toggleHamMenu(false);
            }}
            viewMode={viewMode}
            permissionGroup={permissionGroup}
          />
        </Drawer>
      </Box>
    </>
  );
};

export default connect(
  ({ router, userContext, resources }: RootState) => {
    const resourceCentre = getRCFromState({ userContext, resources } as RootState);
    const adminImpersonating = isAdmin(userContext.mode) && userContext.resourceCentreId;
    const userGroup = userContext.userCreds.userGroups[0];
    return {
      router,
      userGroup,
      mode: userContext.mode,
      resourceCentreId: userContext.resourceCentreId,
      user: { ...userContext.user, role: userContext.mode },
      viewMode: userContext.userCreds.userPreferences?.viewMode,
      permissionGroup:
        userContext && userContext.userCreds.userGroups && userContext.userCreds.userGroups[0],
      subscriptions: resourceCentre?.subscriptions.features,
      adminImpersonating
    };
  },
  (dispatch) => ({
    gotoPath: (route) => {
      dispatch(push(route));
    },
    clearBillDraft: () => dispatch(clearDraft()),
    clearFeedDraftAction: () => dispatch(clearFeedDraft()),
    clearJV: () => dispatch(clearJournalVoucher())
  })
)(SideBar);
